import { useState } from 'react';
import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../components/PageHeader';
import TabHeader from '../components/TabHeader';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import type { SettingsSiteRow } from '../features/settings/types';
import SiteRow from '../features/settings/components/sites/SiteRow';
import SiteModal from '../features/settings/components/sites/SiteModal';
import { useManageSites } from '../features/settings/hooks/useManageSites';
import { useNotification } from '../context/useNotification';
import { SITES_TAB_LABELS as LABELS } from '../utils/labels';
import { hasFullAccess } from '../utils/roles';
import type { MemberRole } from '../types/roles';

interface SitesPageProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface SiteModalState {
  mode: 'create' | 'edit';
  site?: SettingsSiteRow;
}

export default function SitesPage({ orgId, isSuperAdmin, role }: SitesPageProps) {
  const canEdit = hasFullAccess(role, isSuperAdmin);
  const {
    sites,
    archivedSites,
    loadArchived,
    activeProjects,
    addSite,
    updateSite,
    archiveSite,
    unarchiveSite,
    deleteSite,
  } = useManageSites(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [siteModal, setSiteModal] = useState<SiteModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, value: 'active' | 'archived') => {
    setTab(value);
    if (value === 'archived') loadArchived();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteError(null);
    const result = await deleteSite(id);
    if (result === 'ok') {
      notifySuccess(LABELS.deleteSuccess);
    } else if (result === 'has_bookings') {
      setDeleteError(LABELS.deleteErrorHasBookings);
      notifyError(LABELS.deleteErrorHasBookings);
    } else {
      setDeleteError(LABELS.deleteError);
      notifyError(LABELS.deleteError);
    }
  };

  const handleSiteSave = async (
    name: string,
    projectId: string | null,
    color: string,
  ): Promise<boolean> => {
    const ok = siteModal?.mode === 'create'
      ? await addSite(name, projectId, color)
      : siteModal?.site
        ? await updateSite(siteModal.site.id, name, color)
        : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleArchive = async (id: string) => {
    const ok = await archiveSite(id);
    if (ok) notifySuccess(LABELS.archiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleUnarchive = async (id: string) => {
    const ok = await unarchiveSite(id);
    if (ok) notifySuccess(LABELS.unarchiveSuccess); else notifyError(LABELS.mutationError);
  };

  const list = tab === 'active' ? sites : archivedSites;

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-xl mx-auto space-y-6">
          <TabHeader
            title={LABELS.title}
            description={LABELS.subtitle}
            addLabel={canEdit ? LABELS.newSiteButton : undefined}
            onAdd={canEdit ? () => setSiteModal({ mode: 'create' }) : undefined}
            addDisabled={activeProjects.length === 0}
          />

          {activeProjects.length === 0 && (
            <div className="rounded-2xl border border-border p-6 text-center bg-surface">
              <p className="text-sm text-text-secondary">{LABELS.noProjects}</p>
            </div>
          )}

          {deleteError && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 flex items-center gap-2">
              <p className="text-sm text-red-700 flex-1">{deleteError}</p>
              <Tooltip title={LABELS.dismissError}>
                <IconButton
                  size="small"
                  onClick={() => setDeleteError(null)}
                  aria-label={LABELS.dismissError}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <Tabs value={tab} onChange={handleTabChange} className="border-b border-border px-2">
              <Tab label={LABELS.tabActive} value="active" />
              <Tab label={LABELS.tabArchived} value="archived" />
            </Tabs>

            {list.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-6">
                {tab === 'active' ? LABELS.noActive : LABELS.noArchived}
              </p>
            ) : (
              list.map((s, i) => (
                <SiteRow
                  key={s.id}
                  site={s}
                  showDivider={i > 0}
                  onEdit={canEdit ? () => setSiteModal({ mode: 'edit', site: s }) : undefined}
                  onArchive={canEdit && !s.archived ? () => handleArchive(s.id) : undefined}
                  onUnarchive={canEdit && s.archived ? () => handleUnarchive(s.id) : undefined}
                  onDelete={canEdit ? () => setPendingDeleteId(s.id) : undefined}
                />
              ))
            )}
          </div>

          {siteModal && (
            <SiteModal
              site={siteModal.site}
              activeSites={sites}
              activeProjects={activeProjects}
              onClose={() => setSiteModal(null)}
              onSave={handleSiteSave}
            />
          )}

          <ConfirmDeleteDialog
            open={!!pendingDeleteId}
            onClose={() => setPendingDeleteId(null)}
            onConfirm={handleConfirmDelete}
          />
        </div>
      </main>
    </div>
  );
}
